import { JSX, createSignal, Show } from 'solid-js';
import {
  isPointCloudLoading,
  isPointCloudVisible,
  isTrajectoryLoading,
  isTrajectoryVisible,
  MissionItem,
  useApiServices,
  useDataContext,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import { t } from '@trimble/i18n';
import { DeleteConfirmationModal } from '@tcweb/connect-ui';
import { NotificationEventBus } from '@trimble/solidjs-notifications';
import { useConnectProject } from '@trimble/connect-project-providers';

export interface MissionToDelete {
  missionTrn: string;
  name: string;
}

export interface RunToDelete {
  runId: string;
  missionTrn: string;
  name: string;
}

interface UseMissionTreeDeleteResult {
  setMissionToDelete(mission: MissionToDelete | null): void;
  setRunToDelete(run: RunToDelete | null): void;
  deleteModals(): JSX.Element;
}

export function useMissionTreeDelete(): UseMissionTreeDeleteResult {
  const { dataState, dataLoadingService } = useDataContext();
  const { selectedTenantTrn, selectedProject } = useConnectProject();
  const { mobileMappingApi } = useApiServices();

  const [missionToDelete, setMissionToDelete] =
    createSignal<MissionToDelete | null>(null);
  const [runToDelete, setRunToDelete] = createSignal<RunToDelete | null>(null);

  const deleteMissionMutation = mobileMappingApi.deleteMissionMutation();
  const deleteRunMutation = mobileMappingApi.deleteRunMutation();

  const isPathLoading = (alignmentTrn: string): boolean =>
    isTrajectoryLoading(dataState, alignmentTrn);

  const isPathVisible = (alignmentTrn: string): boolean =>
    isTrajectoryVisible(dataState, alignmentTrn);

  const isRunLoading = (pointCloudTrn: string): boolean =>
    isPointCloudLoading(dataState, pointCloudTrn);

  const isRunVisible = (pointCloudTrn: string): boolean =>
    isPointCloudVisible(dataState, pointCloudTrn);

  const findMissionByTrn = (missionTrn: string): MissionItem | undefined => {
    if (dataState.mission.state !== LoadState.LoadComplete) return undefined;
    return Object.values(dataState.mission.items).find(
      (m) => m.missionTrn === missionTrn
    );
  };

  const unloadFrom3DViewer = (missionTrn: string, runId?: string): void => {
    const mission = findMissionByTrn(missionTrn);
    if (!mission) return;

    if (
      mission.pathId != null &&
      (isPathLoading(mission.pathId) || isPathVisible(mission.pathId))
    ) {
      dataLoadingService.unload('trajectory', mission.pathId);
    }

    const runsToUnload = runId
      ? mission.runs.filter((r) => r.runId === runId)
      : mission.runs;

    for (const run of runsToUnload) {
      if (isRunLoading(run.id) || isRunVisible(run.id)) {
        dataLoadingService.unload('pointCloud', run.id);
      }
    }
  };

  const handleConfirmDeleteMission = (): void => {
    const mission = missionToDelete();
    if (!mission) return;

    unloadFrom3DViewer(mission.missionTrn);
    deleteMissionMutation.mutate(
      {
        ...mission,
        tenantTrn: selectedTenantTrn()!,
        projectId: selectedProject()?.id,
      },
      {
        onSuccess: (_data, variables) => {
          setMissionToDelete(null);
          NotificationEventBus.emit({
            type: 'info',
            message: t('mobileMapping.missions.deleteSuccess', {
              name: variables.name,
            }),
          });
        },
        onError: (_error, variables) => {
          setMissionToDelete(null);
          NotificationEventBus.emit({
            type: 'error',
            message: t('mobileMapping.missions.deleteFailed', {
              name: variables.name,
            }),
          });
        },
      }
    );
  };

  const handleConfirmDeleteRun = (): void => {
    const run = runToDelete();
    if (!run) return;

    unloadFrom3DViewer(run.missionTrn, run.runId);
    deleteRunMutation.mutate(
      {
        ...run,
        tenantTrn: selectedTenantTrn()!,
        projectId: selectedProject()?.id,
      },
      {
        onSuccess: (_data, variables) => {
          setRunToDelete(null);
          NotificationEventBus.emit({
            type: 'info',
            message: t('mobileMapping.runs.deleteSuccess', {
              name: variables.name,
            }),
          });
        },
        onError: (_error, variables) => {
          setRunToDelete(null);
          NotificationEventBus.emit({
            type: 'error',
            message: t('mobileMapping.runs.deleteFailed', {
              name: variables.name,
            }),
          });
        },
      }
    );
  };

  const deleteModals = (): JSX.Element => (
    <>
      <Show when={missionToDelete()}>
        <DeleteConfirmationModal
          title={t('general.delete')}
          message={t('mobileMapping.missions.confirmPermanentDelete')}
          waiting={deleteMissionMutation.isPending}
          onCancel={() => setMissionToDelete(null)}
          onConfirm={handleConfirmDeleteMission}
        />
      </Show>
      <Show when={runToDelete()}>
        <DeleteConfirmationModal
          title={t('general.delete')}
          message={t('mobileMapping.runs.confirmPermanentDelete')}
          waiting={deleteRunMutation.isPending}
          onCancel={() => setRunToDelete(null)}
          onConfirm={handleConfirmDeleteRun}
        />
      </Show>
    </>
  );

  return {
    setMissionToDelete,
    setRunToDelete,
    deleteModals,
  };
}
